import React from 'react';
import styled from 'styled-components/macro';
import { Typography } from '@deeptrue-frontend/ui';
import Legend from './Arrows/Arrows';

import Xarrow from 'react-xarrows';
const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const BrandBox = styled.div`
  display: flex;
  height: 8rem;
  position: relative;
`;

const BrandTitle = styled(Typography)`
  position: absolute;
  bottom: 0.5rem;
  left: 1rem;
  font-weight: 600;
`;

const LegendWrapper = styled.div``;

interface SubDataType {
  subNumber: number | null;
  mainNumber: number;
}

interface DataType {
  data: Array<SubDataType>;
  isHighlighted: boolean;
  name: string;
}

interface Props {
  data: Array<DataType>;
  legend: any;
}

const mainColor = '#fce331';
const greenPalette = [
  '#75b09c',
  '#9ec8ba',
  '#c8dfd7',
  '#e0efe9',
  '#dbdbdb',
  '#7f7f7f',
];

const Section = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  padding: 1rem 2rem;
  max-width: 25%;
`;

const WhiteBox = styled.div<{ position: 'first' | 'middle' | 'last' }>`
  background-color: white;
  border: 1px solid black;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.5rem;
  height: 2.5rem;
  position: absolute;
  bottom: 2rem;
  left: 0;

  ${({ position }) => {
    switch (position) {
      case 'first':
        return `
          left: 2rem;
        `;
      case 'middle':
        return `
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'last':
        return `
            left: unset;
            right: 2rem;

          `;
    }
  }}
`;
const BlackBox = styled.div<{ position: 'first' | 'middle' | 'last' }>`
  background-color: black;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 2rem;
  position: absolute;
  top: 1rem;

  p {
    color: white;
  }

  ${({ position }) => {
    switch (position) {
      case 'first':
        return `
          right: 2rem;
        `;
      case 'middle':
        return `
          right: 0;
         
        `;
      case 'last':
        return `
            right:-3rem;

          `;
    }
  }}
`;

const checkPosition = (index: number, elemNr: number, isFullData: boolean) => {
  if (index === 0) return 'first';

  if (index + 1 === elemNr && isFullData) return 'last';
  if (index + 1 === elemNr && !isFullData) return 'middle';
  return 'middle';
};

const BrandFunnel = ({ data, legend }: Props) => {
  return (
    <Container>
      <LegendWrapper>
        <Legend legend={legend} />
      </LegendWrapper>

      {data.map(({ data, name, isHighlighted }: DataType, index: number) => {
        return (
          <BrandBox
            key={name}
            style={{
              backgroundColor: isHighlighted ? mainColor : greenPalette[index],
            }}
          >
            {data.map((subData: SubDataType, index: number) => {
              const isFullData = data.length > 3;
              const isLastSection = typeof subData.subNumber === 'number';
              const position = checkPosition(index, data.length, isFullData);
              const isLastBlackBox =
                data[index + 1] && data[index + 1].subNumber;

              const isFirstWhiteBox = position === 'first';

              const shouldRenderArrow = data[index + 2] !== undefined;

              return (
                <Section key={index}>
                  {isLastSection && (
                    <BlackBox
                      id={`${name}-black-${index}`}
                      position={
                        !isLastBlackBox && isFullData ? 'last' : position
                      }
                    >
                      <Typography weight="bold">{subData.subNumber}</Typography>
                    </BlackBox>
                  )}

                  <WhiteBox position={position} id={`${name}-white-${index}`}>
                    <Typography weight="bold">{subData.mainNumber}</Typography>
                  </WhiteBox>
                  {isFirstWhiteBox && (
                    <Xarrow
                      start={`${name}-white-${index}`}
                      end={`${name}-black-${index}`}
                      color="black"
                      curveness={0.4}
                      startAnchor="top"
                      endAnchor="left"
                      path="grid"
                      headSize={0}
                    />
                  )}
                  <Xarrow
                    start={`${name}-black-${index}`}
                    end={`${name}-black-${index + 1}`}
                    color="black"
                    curveness={0.4}
                    startAnchor="right"
                    endAnchor="left"
                    path="grid"
                    headSize={0}
                  />
                  {!isLastBlackBox && (
                    <Xarrow
                      start={`${name}-black-${index}`}
                      end={`${name}-white-${index + 1}`}
                      color="black"
                      curveness={0.4}
                      headSize={3}
                      startAnchor="right"
                      endAnchor="top"
                      path="grid"
                    />
                  )}
                  {shouldRenderArrow && (
                    <Xarrow
                      start={`${name}-black-${index}`}
                      end={`${name}-white-${index + 1}`}
                      color="black"
                      curveness={0.4}
                      headSize={3}
                    />
                  )}
                </Section>
              );
            })}

            <BrandTitle>{name}</BrandTitle>
          </BrandBox>
        );
      })}
    </Container>
  );
};

export default BrandFunnel;
